import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
  Snackbar,
  Button
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  RestartAlt as RestartIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import DropZone from './DropZone';
import ProcessingSteps from './ProcessingSteps';
import AnalysisDisplay from './AnalysisDisplay';
import MaskGallery from './MaskGallery';
import CroppedResults from './CroppedResults';
import processService from '../services/processService';

function AutoCrop() {
  const [jobId, setJobId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  
  // Results state
  const [mainObject, setMainObject] = useState(null);
  const [masks, setMasks] = useState(null);
  const [totalMasks, setTotalMasks] = useState(0);
  const [displayedMasks, setDisplayedMasks] = useState(0);
  const [selectedMaskIndex, setSelectedMaskIndex] = useState(-1);
  const [crops, setCrops] = useState(null);
  const [modelName, setModelName] = useState('YOLO');
  const [originalImage, setOriginalImage] = useState(null);
  const [bboxes, setBboxes] = useState(null);
  
  // Refs for scrolling to sections
  const analysisRef = useRef(null);
  const cropsRef = useRef(null);
  
  // Scroll to AI Analysis when mainObject is first set
  useEffect(() => {
    if (mainObject && analysisRef.current) {
      console.log('Scrolling to AI Analysis section');
      setTimeout(() => {
        analysisRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 500);
    }
  }, [mainObject]); // Only trigger when mainObject changes from null to a value
  
  // Scroll to Cropped Variations when mask is selected
  useEffect(() => {
    if (selectedMaskIndex > -1 && cropsRef.current) {
      console.log('Scrolling to Cropped Variations section');
      setTimeout(() => {
        cropsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 500);
    }
  }, [selectedMaskIndex]); // Only trigger when selectedMaskIndex changes
  
  const resetState = () => {
    setJobId(null);
    setIsProcessing(false);
    setCurrentStage(null);
    setProgress(0);
    setMessage('');
    setError(null);
    setMainObject(null);
    setMasks(null);
    setTotalMasks(0);
    setDisplayedMasks(0);
    setSelectedMaskIndex(-1);
    setCrops(null);
    setModelName('YOLO');
    setOriginalImage(null);
    setBboxes(null);
  };
  
  const handleFileAccepted = useCallback(async (file) => {
    try {
      setIsProcessing(true);
      setError(null);
      setCurrentStage('uploaded');
      setMessage('Processing image...');
      
      // Process image using the new stateless API
      const result = await processService.processImage(
        file,
        (update) => {
          // Handle progress updates
          setCurrentStage(update.stage);
          setProgress(update.progress || 0);
          setMessage(update.message || '');
          
          // Handle stage-specific data
          if (update.data) {
            if (update.data.mainObject) {
              setMainObject(update.data.mainObject);
            }
            if (update.data.masks) {
              console.log('Received masks:', update.data.masks);
              setMasks(update.data.masks);
            }
            if (update.data.totalMasks !== undefined) {
              setTotalMasks(update.data.totalMasks);
            }
            if (update.data.displayedMasks !== undefined) {
              setDisplayedMasks(update.data.displayedMasks);
            }
            if (update.data.modelName !== undefined) {
              setModelName(update.data.modelName);
            }
            if (update.data.selectedMaskIndex !== undefined) {
              setSelectedMaskIndex(update.data.selectedMaskIndex);
            }
            if (update.data.crops) {
              setCrops(update.data.crops);
              // Set a dummy jobId for the download functionality
              setJobId('current');
            }
          }
        }
      );
      
      // Store the original image and bboxes from the result
      if (result) {
        setOriginalImage(result.originalImage);
        setBboxes(result.bboxes);
      }
      
      // Processing complete
      setIsProcessing(false);
      
    } catch (err) {
      console.error('Processing error:', err);
      setError(err.message || 'Failed to process image');
      setIsProcessing(false);
    }
  }, []);
  
  return (
    <Container maxWidth="lg">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            <AutoAwesomeIcon 
              sx={{ 
                fontSize: 48, 
                color: 'primary.main',
                mr: 2
              }} 
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent'
              }}
            >
              AutoCrop AI
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Upload an image to automatically generate perfectly cropped variations
          </Typography>
        </Box>
      </motion.div>
      
      {/* Reset Button */}
      {(currentStage && !isProcessing) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<RestartIcon />}
            onClick={resetState}
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.light',
                bgcolor: 'rgba(99, 102, 241, 0.1)'
              }
            }}
          >
            Process Another Image
          </Button>
        </Box>
      )}
      
      {/* Drop Zone */}
      {!currentStage && (
        <DropZone 
          onFileAccepted={handleFileAccepted}
          isProcessing={isProcessing}
        />
      )}
      
      {/* Processing Steps */}
      {currentStage && (
        <ProcessingSteps
          currentStage={currentStage}
          progress={progress}
          message={message}
        />
      )}
      
      {/* Results Section */}
      {currentStage && (
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Analysis Display */}
          {(currentStage === 'analyzing' || mainObject) && (
            <Box ref={analysisRef}>
              <AnalysisDisplay
                mainObject={mainObject}
                isLoading={currentStage === 'analyzing' && !mainObject}
              />
            </Box>
          )}
          
          {/* Mask Gallery - Show from segmenting phase onwards */}
          {(currentStage && ['segmenting', 'selecting', 'cropping', 'complete'].includes(currentStage)) && (
            <MaskGallery
              masks={masks}
              selectedIndex={selectedMaskIndex}
              isLoading={currentStage === 'segmenting' && !masks}
              stage={currentStage}
              totalMasks={totalMasks}
              displayedMasks={displayedMasks}
              modelName={modelName}
            />
          )}
          
          {/* Cropped Results */}
          {(currentStage === 'cropping' || currentStage === 'complete' || crops) && (
            <Box ref={cropsRef}>
              <CroppedResults
                crops={crops}
                jobId={jobId}
                isLoading={currentStage === 'cropping' && !crops}
              />
            </Box>
          )}
        </Box>
      )}
      
      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AutoCrop;